import React, { useState, useEffect, useRef } from "react";
import { getUsersClients } from "../modules/Admin";
import { getretrieveReferralsDetails } from "../modules/Referrals";
import { showSpinner, closeSpinner, modal, alert } from "../modules/Dialogs";
import { sessionExpired } from "../config/GeneralConfig";
import PropTypes from "prop-types";
import withNavigate from "./withNavigate";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import Button from "@mui/material/Button";
import {
  getSafariCompatibleDrawerSx,
  getSafariCompatibleBoxSx,
} from "../modules/DrawerUtils";
import { toScreen } from "../modules/Routing";
import Pagination from "./Pagination";
import moment from "moment-timezone";

const referralCountFromClient = (client) =>
  client?.referralCount ?? client?.referrals ?? client?.totalReferrals ?? 0;

const Referrals = ({ userDetails, navigate }) => {
  const jwtToken = userDetails.jwttoken;

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedReferral, setSelectedReferral] = useState(null);
  const [referredClients, setReferredClients] = useState([]);
  const drawerRef = useRef(null);

  const [searchValue, setSearchValue] = useState(null);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState(
    window.history.state?.usr?.selectedFilter ?? "all",
  );
  const [pageSize, setPageSize] = useState(15);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [inputValue, setInputValue] = useState(1);

  const [sort, setSort] = useState("");
  const [referrals, setReferrals] = useState([]);

  useEffect(() => {
    setInputValue(page);
  }, [page]);

  useEffect(() => {
    getReferrals();
  }, [filter, page, pageSize, sort, search]);

  const getReferrals = () => {
    showSpinner();
    const queryParams = {
      filter: filter && filter !== "all" ? filter : undefined,
      search,
      page,
      limit: pageSize,
      sort,
    };
    getUsersClients(queryParams, getReferrals_done, jwtToken);
  };

  const getReferrals_done = (resStatus, responseString, resJson) => {
    closeSpinner();

    if (resStatus === 401) {
      modal(sessionExpired);
      toScreen(false, navigate, "/logout");
    } else if (resStatus === "400") {
      setReferrals([]);
      alert(responseString);
    } else if (resStatus === 200) {
      const total = resJson?.totalRecords ?? 0;
      setTotalPages(Math.max(1, Math.ceil(total / pageSize)));
      setReferrals(resJson?.data || []);
    }
  };

  const loadReferralDetails = (userId) => {
    if (!userId) {
      setReferredClients([]);
      return;
    }
    showSpinner();
    getretrieveReferralsDetails(
      { page: 1, limit: 50, userId },
      (resStatus, responseString, resJson) => {
        closeSpinner();
        if (resStatus === 401) {
          modal(sessionExpired);
          toScreen(false, navigate, "/logout");
        } else if (resStatus === "400") {
          setReferredClients([]);
          alert(responseString);
        } else if (resStatus === 200) {
          setReferredClients(resJson?.data);
        }
      },
      jwtToken,
    );
  };

  const toggleDrawer =
    (open, referral = null) =>
    (event) => {
      if (
        event?.type === "keydown" &&
        (event.key === "Tab" || event.key === "Shift")
      ) {
        return;
      }

      setDrawerOpen(open);
      if (open && referral) {
        setSelectedReferral(referral);
        setReferredClients([]);
        loadReferralDetails(referral?._id);
      }
      if (!open) {
        setSelectedReferral(null);
        setReferredClients([]);
      }
    };

  const searchJob = () => {
    const raw = searchValue == null ? "" : String(searchValue);
    const trimmed = raw.trim();
    setSearch(trimmed);
    setFilter(trimmed === "" ? "all" : null);
    setPage(1);
  };

  const sortReferrals = (event, field) => {
    if (event) event.preventDefault();

    const sortFields = {
      name: { asc: "name=1", desc: "name=-1" },
      referrals: { asc: "referrals=1", desc: "referrals=-1" },
      startDate: { asc: "startDate=1", desc: "startDate=-1" },
      uses: { asc: "uses=1", desc: "uses=-1" },
      endDate: { asc: "endDate=1", desc: "endDate=-1" },
    };

    const currentSort = sortFields[field];
    if (!currentSort) return;

    const newSort =
      sort === currentSort.asc ? currentSort.desc : currentSort.asc;

    setSearch("");
    setSearchValue(null);
    setFilter("all");
    setSort(newSort);
    setPage(1);
  };

  const renderReferrals = (referral, index) => {
    const userName = referral?.accountInfo?.username ?? "";
    const firstName = referral?.accountInfo?.firstname ?? "";
    const lastName = referral?.accountInfo?.surname ?? "";
    const shortId = referral?.shortId ?? "";

    return (
      <div
        className={`c-list-entry align-items-center`}
        key={referral?._id || index}
      >
        <div className="c-list-entry__referralClientName">
          <b>{userName}</b>
          <p className="mb-0">{`${firstName} ${lastName}`}</p>
          <p className="mb-0">{`${shortId}`}</p>
        </div>
        <div className="c-list-entry__jobdate">{referral?.referrals || 0}</div>
        <Button
          className="btn btn-primary btn-sm"
          onClick={toggleDrawer(true, referral)}
        >
          Details
        </Button>
      </div>
    );
  };

  const drawerContent = () => {
    const sel = selectedReferral;
    const firstName = sel?.accountInfo?.firstname ?? "";
    const lastName = sel?.accountInfo?.surname ?? "";
    const userName = sel?.accountInfo?.username ?? "";
    const shortId = sel?.shortId ?? "";
    const totalReferrals = referralCountFromClient(sel);

    return (
      <Box
        sx={getSafariCompatibleBoxSx()}
        role="presentation"
        onKeyDown={toggleDrawer(false)}
      >
        <div ref={drawerRef} className="right-modal">
          <div className="modal-header">
            <div className="modal-title">
              <h2>Client Details</h2>
              <button className="modal-close-btn" onClick={toggleDrawer(false)}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="lucide lucide-x h-5 w-5"
                >
                  <path d="M18 6 6 18"></path>
                  <path d="m6 6 12 12"></path>
                </svg>
              </button>
            </div>
          </div>
          {sel && (
            <div className="modal-body">
              <div className="client-main-info">
                <div>
                  <h3>
                    {firstName} {lastName}
                  </h3>
                  <p className="client-id">
                    {firstName}-{shortId}
                  </p>
                </div>
                <button
                  className="c-btn c-btn--black c-btn--small"
                  onClick={(e) => toScreen(e, navigate, "/client/" + shortId)}
                >
                  <span className="u-before-icon-avatar-fill text-capitalize"></span>
                  Client Details
                </button>
              </div>

              <div className="referral-summary">
                <div className="referral-box">
                  <p className="referral-title mb-0">Total Referrals</p>
                  <p className="referral-count mb-0">{totalReferrals}</p>
                </div>
              </div>

              <div className="referred-clients-table">
                <table>
                  <thead>
                    <tr>
                      <th>Referred Clients</th>
                      <th>Signed Up</th>
                      <th>Posted Jobs</th>
                      <th>Booked Jobs</th>
                    </tr>
                  </thead>
                  <tbody>
                    {referredClients.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="u-text-align-center">
                          No referred clients found
                        </td>
                      </tr>
                    ) : (
                      referredClients.map((client, idx) => {
                        const refList = client?.referredUserDetails;
                        const refOne = Array.isArray(refList)
                          ? refList[0]
                          : refList;
                        const name =
                          refOne?.firstname ??
                          client?.firstname ??
                          client?.accountInfo?.firstname ??
                          "";
                        const lastName =
                          refOne?.lastname ??
                          client?.lastname ??
                          client?.accountInfo?.surname ??
                          "";
                        const fullName = `${name} ${lastName}`.trim();
                        const sid =
                          refOne?.shortId ??
                          refOne?.short_id ??
                          client?.shortId ??
                          client?.short_id ??
                          "";
                        const dateRaw =
                          refOne?.createdAt ??
                          client?.createdAt ??
                          client?._signedUpAt ??
                          client?.creationDate ??
                          client?.signedUpDate;
                        const dateLabel = dateRaw
                          ? moment(dateRaw)
                              .tz("Europe/London")
                              .format("DD MMM YYYY")
                          : "—";
                        const posted =
                          refOne?.postedJobs ?? client?.postedJobs ?? 0;
                        const booked =
                          refOne?.bookedJobs ?? client?.bookedJobs ?? 0;

                        return (
                          <tr key={client?._id || sid || idx}>
                            <td className="client-name-cell">
                              {sid ? (
                                <a
                                  href={`/client/${sid}`}
                                  onClick={(e) =>
                                    toScreen(e, navigate, `/client/${sid}`)
                                  }
                                >
                                  {fullName}
                                </a>
                              ) : (
                                fullName
                              )}
                            </td>
                            <td>{dateLabel}</td>
                            <td>{posted}</td>
                            <td>{booked}</td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </Box>
    );
  };

  const handleInputChange = (event) => {
    const { name, value, type, checked } = event.target;
    const inputVal = type === "checkbox" ? checked : value;

    if (name === "search") {
      setSearchValue(inputVal);
    } else if (name === "filter") {
      setSearch("");
      setSearchValue(null);
      setFilter(inputVal);
      setPage(1);
    }
  };

  const getSortClass = (key) => {
    if (sort === `${key}=1`) {
      return "c-list-filter__button c-list-filter__button--icon-up";
    } else if (sort === `${key}=-1`) {
      return "c-list-filter__button c-list-filter__button--icon-down";
    }
    return "c-list-filter__button c-list-filter__button--faded";
  };

  return (
    <div className="l-base-layout__content">
      <h1>Admin / Referrals</h1>
      <div className="c-hr"></div>

      <div className="c-subheader-info-panel">
        <div className="c-subheader-info-panel__search">
          <form
            className="d-flex align-items-center"
            onSubmit={(e) => {
              e.preventDefault();
              searchJob();
            }}
          >
            <div className="search-input-block">
              <input
                type="text"
                value={searchValue || ""}
                onChange={handleInputChange}
                name="search"
                placeholder="Search Referrals..."
                className="c-subheader-info-panel__input"
              />
            </div>

            <button
              type="button"
              className="c-btn-outline c-btn-outline--white c-btn-outline--small"
              onClick={searchJob}
            >
              Search
            </button>
          </form>
        </div>
      </div>

      <div className="c-hr"></div>
      <div className="l-row">
        <div className="l-col-100">
          <div className="c-hr u-margin-bottom-0"></div>

          <div>
            <div className="c-list-entry c-list-entry__header ">
              <div className="c-list-entry__referralClientName c-list-filter">
                <button
                  className={getSortClass("name")}
                  onClick={(e) => sortReferrals(e, "name")}
                >
                  Client name
                </button>
              </div>
              <div className="c-list-entry__jobdate c-list-filter">
                <button
                  className={getSortClass("referrals")}
                  onClick={(e) => sortReferrals(e, "referrals")}
                >
                  Referrals
                </button>
              </div>
            </div>
          </div>

          <div>
            {referrals?.length > 0 ? (
              referrals?.map(renderReferrals)
            ) : (
              <div className="u-text-align-center u-margin-top-bottom-m u-color-red">
                No Referrals found
              </div>
            )}
          </div>

          <Drawer
            anchor="right"
            open={drawerOpen}
            onClose={toggleDrawer(false)}
            ModalProps={{
              BackdropProps: {
                style: { backgroundColor: "rgba(0, 0, 0, 0.1)" },
              },
            }}
            sx={getSafariCompatibleDrawerSx(600)}
          >
            {drawerContent()}
          </Drawer>

          <div className="c-hr u-margin-bottom-0"></div>

          <Pagination
            pageSize={pageSize}
            totalPages={totalPages}
            page={page}
            setPage={setPage}
            inputValue={inputValue}
            setInputValue={setInputValue}
            setPageSize={setPageSize}
          />
        </div>
      </div>
    </div>
  );
};

Referrals.propTypes = {
  userDetails: PropTypes.object.isRequired,
  navigate: PropTypes.func.isRequired,
};

export default withNavigate(Referrals);
